import axios from "axios";
import {
  DSC_MESSAGES,
  DSC_SIGNER,
  DSC_STAMP_DETAILS,
  DscStampPlacement,
  DscToken,
} from "../config/levelThreeSigning";
import { traceDscCall } from "./dscSigningLog";
import { DscStampStyle } from "./dscStampConfig";

/**
 * Every call to the signer program on the officer's computer lives in this
 * file, and its address, call names and field names come from DSC_SIGNER in
 * config/levelThreeSigning.ts. Plain axios is used on purpose: the shared
 * _axios instance carries this application's login details and interceptors,
 * which have no business going to another program.
 */

export type DscSigner = {
  name: string;
  email?: string;
};

type SignPdfInput = {
  pdf: Blob;
  token: DscToken;
  /** The token PIN. It is sent to the signer program only and is never stored. */
  pin: string;
  signer: DscSigner;
  placement: DscStampPlacement;
  /**
   * The stamp's look and fixed lines from the server's
   * config/dsc-stamp.json. A value left out is not sent, and the signer
   * program uses its own config.json for it.
   */
  style?: DscStampStyle;
  application?: any;
  /** Which document is being signed; written to the DSC trace only. */
  document?: string;
};

export type SignedPdf = {
  pdf: Blob;
  /** The name on the USB token, as the signer program read it. */
  signerName: string;
};

/** Turns the base64 text the signer program returns into a PDF blob. */
function base64ToPdfBlob(base64: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }

  return new Blob([bytes], { type: "application/pdf" });
}

/**
 * The message to show the officer for a failed call: the signer program's own
 * message when it sent one, and a plain hint when it could not be reached.
 */
export function getDscErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const message = (error.response?.data as { message?: string } | undefined)
      ?.message;

    if (message) return message;
    if (!error.response) return DSC_MESSAGES.SIGNER_NOT_REACHABLE;
  }

  return error instanceof Error && error.message
    ? error.message
    : "Signing failed. Please try again.";
}

/**
 * One call to the signer program. What was sent and what came back is
 * reported to the backend's DSC trace, so a signing done on an officer's
 * computer can be followed from the server. `sent` is the readable copy of
 * the request: it never holds the PIN, and the signed PDF is left out of the
 * reply, with only its length recorded.
 */
async function callSigner(
  stage: string,
  path: string,
  body: unknown,
  sent: Record<string, unknown>
) {
  try {
    const response = await axios.post(`${DSC_SIGNER.baseUrl}${path}`, body, {
      responseType: "json",
      timeout: DSC_SIGNER.timeoutMs,
      validateStatus: (status) => status < 500,
    });
    const { [DSC_SIGNER.signedPdfField]: signedPdf, ...reply } =
      response.data ?? {};

    traceDscCall({
      stage,
      sent,
      httpStatus: response.status,
      reply,
      ...(signedPdf && { signedPdfBase64Length: String(signedPdf).length }),
    });

    return response;
  } catch (error) {
    traceDscCall({ stage, sent, error: getDscErrorMessage(error) });
    throw error;
  }
}

/** Asks the signer program whether the chosen USB token is plugged in and readable. */
export async function checkDscToken(token: DscToken): Promise<void> {
  /** A signer program with no token check has an empty checkTokenPath. */
  if (!DSC_SIGNER.checkTokenPath) return;

  /*
   * BEFORE - the call was made here directly and left no record:
   * const response = await axios.post(
   *   `${DSC_SIGNER.baseUrl}${DSC_SIGNER.checkTokenPath}`,
   *   { name: token.name, value: token.value, driverPath: token.driverPath },
   *   { timeout: DSC_SIGNER.timeoutMs, validateStatus: (status) => status < 500 }
   * );
   */
  const tokenDetails = {
    name: token.name,
    value: token.value,
    driverPath: token.driverPath,
  };
  const response = await callSigner(
    "check-token",
    DSC_SIGNER.checkTokenPath,
    tokenDetails,
    tokenDetails
  );

  if (response.data?.status === "error") {
    throw new Error(response.data?.message || "The USB token was not detected.");
  }
}

/** Sends the PDF to the signer program and returns the signed PDF. */
export async function signPdfWithDsc({
  pdf,
  token,
  pin,
  signer,
  placement,
  style = {},
  application,
  document,
}: SignPdfInput): Promise<SignedPdf> {
  const formData = new FormData();

  formData.append(DSC_SIGNER.fileField, pdf, "document.pdf");
  
  /*
   * BEFORE - the signer program had one free line (designation), so Reason,
   * Designation, Location and Application Number were packed into it, and a
   * date was built here that the signer program never read:
   *
   * const applicationNumber = application?.applicationId || application?.application_id || "";
   * const now = new Date();
   * const dateStr = `${now.getDate().toString().padStart(2, '0')}-...`;
   *
   *   signer_name: signer.name,
   *   contact: signer.email ?? "",
   *   designation: `Reason: Approved\nDesignation: ${placement.designation}\nLocation: Bahadura Nagarpanchayat\nApplication Number: ${applicationNumber}`,
   *
   * The signer program now draws the whole stamp ("Signature valid", the
   * tick and one line per value) and takes each value as its own field. The
   * name comes from the USB token and the date from the moment of signing.
   */
  /**
   * Everything the signer program is told, apart from the PIN.
   *
   * BEFORE - reason, location and project code came from the built-in
   * DSC_STAMP_DETAILS only, and the stamp's look (heading, tick, font size,
   * lines...) was set in each officer's config.json:
   *   reason: DSC_STAMP_DETAILS.reason,
   *   location: DSC_STAMP_DETAILS.location,
   *   project_code: DSC_STAMP_DETAILS.projectCode,
   *
   * The look now comes from the server as well (signer program 1.2.0 and
   * later). An older signer program ignores the fields it does not know.
   */
  const signingDetails = {
    name: token.name,
    value: token.value,
    driverPath: token.driverPath,
    contact: signer.email ?? "",
    reason: style.reason ?? DSC_STAMP_DETAILS.reason,
    designation: placement.designation,
    location: style.location ?? DSC_STAMP_DETAILS.location,
    project_code: style.projectCode ?? DSC_STAMP_DETAILS.projectCode,
    anchor_text: placement.anchorText,
    anchor_gap: placement.anchorGap,
    page: placement.page,
    signaturebox: placement.signaturebox,
    heading: style.heading,
    show_tick: style.showTick,
    tick_size: style.tickSize,
    font_size: style.fontSize,
    border_width: style.borderWidth,
    timestamp_format: style.timestampFormat,
    anchor_align: style.anchorAlign,
    heading_size: style.headingSize,
    text_align: style.textAlign,
    text: style.lines?.join("\n"),
  };

  formData.append(
    DSC_SIGNER.metadataField,
    JSON.stringify({ ...signingDetails, pin })
  );

  /*
   * BEFORE - the call was made here directly and left no record:
   * const response = await axios.post(
   *   `${DSC_SIGNER.baseUrl}${DSC_SIGNER.signPath}`,
   *   formData,
   *   { responseType: "json", timeout: DSC_SIGNER.timeoutMs, validateStatus: (status) => status < 500 }
   * );
   */
  const response = await callSigner("sign", DSC_SIGNER.signPath, formData, {
    ...signingDetails,
    document,
    applicationId: application?.id,
    applicationNumber: application?.applicationId,
    unsignedPdfBytes: pdf.size,
  });

  if (response.data?.status === "error") {
    throw new Error(response.data?.message || "Signing failed.");
  }

  const signedPdfBase64 = response.data?.[DSC_SIGNER.signedPdfField];

  if (!signedPdfBase64) {
    throw new Error("The signer program did not return a signed document.");
  }

  /** BEFORE - only the PDF was returned: return base64ToPdfBlob(signedPdfBase64); */
  return {
    pdf: base64ToPdfBlob(signedPdfBase64),
    signerName: response.data?.signer ?? "",
  };
}
